// MATERIAL UI
import { Button, Grid, Typography } from "@mui/material";

// COMPONENTS
import { StyledAvatar } from "src/components/wallets/common/styles/CryptoWithdrawalStyled";
import { ButtonContainer } from "./ButtonContainer";

// STYLES
import { PaymentStyles } from "./Payment.styles";

// REDUX
import {
  useBeneficiarySlice,
  setStep,
  initialState,
  setBeneficiaryState,
} from "src/store/features/beneficiary.slice";

// ICONS
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { trackEvent } from "src/Amplitude";
import { useDispatch } from "react-redux";

export const Complete = (): JSX.Element => {
  const dispatch = useDispatch();

  const classes = PaymentStyles();

  /*********************
   * BENEFICIARY STATE *
   *********************/
  const {
    currency,
    beneficiaryName,
    beneficiaryAddress,
    bankName,
    bankAddress,
    swiftCode,
    transitNumber,
    institutionNumber,
    abaRoutingNumber,
    ibanAccNumber,
    sortCodeOrBsb,
    identityType,
    accountType,
  } = useBeneficiarySlice();

  /************
   * HANDLERS *
   ************/
  const handleGoBack = () => {
    dispatch(setStep("ViewDetails"));
    trackEvent("Client went back to payments page from completed page", {}, "Payment");
  };

  const handleAddDetails = () => {
    dispatch(setBeneficiaryState({ ...initialState, step: "AddDetails" }));
    trackEvent("Client clicked on Add Bank Details", {}, "Payment");
  };

  const beneficiaryAddressValues = Object.values(beneficiaryAddress).filter((value) => value);
  const bankAddressValues = Object.values(bankAddress).filter((value) => value);

  const isGbpOrEur = currency.value === "GBP" || currency.value === "EUR";
  const isUsdOrGbp = currency.value === "USD" || currency.value === "GBP";
  const isAud = currency.value === "AUD";
  const isUsd = currency.value === "USD";
  const isGbp = currency.value === "GBP";

  return (
    <>
      <Grid
        width="100%"
        sx={{ backgroundColor: "#F0FEF9", padding: 1.5, borderRadius: "8px" }}
        display="flex"
        alignItems="center"
        marginTop={"16px !important"}>
        <CheckCircleIcon sx={{ width: "14px", height: "14px", marginRight: "8px" }} />
        <Typography fontSize="14px">
          Your bank details have been successfully added to our Portal! If you have any concerns,
          feel free to reach out to our Support team.
        </Typography>
      </Grid>
      <Grid item width="100%" paddingTop={"24px !important"}>
        <Typography className={classes.bankManagementSubHeader} style={{ textAlign: "center" }}>
          Review Bank Details
        </Typography>
      </Grid>
      <Grid item width="100%" paddingTop={"12px !important"}>
        <Typography className={classes.summaryHeader}>Currency</Typography>
        <Grid item display="flex" flexDirection="row" alignItems="center">
          <StyledAvatar src={currency.image} />
          <Typography>{currency.value}</Typography>
        </Grid>
      </Grid>
      <Grid item width="100%">
        <Typography className={classes.summaryHeader}>Account Holder Name</Typography>
        <Typography>{beneficiaryName}</Typography>
      </Grid>
      <Grid item width="100%">
        <Typography className={classes.summaryHeader}>Account Holder Address</Typography>
        <Grid item width="100%">
          <Typography>{beneficiaryAddressValues.join(", ")}</Typography>
        </Grid>
      </Grid>
      <Grid
        container
        item
        justifyContent="space-between"
        flexDirection="row"
        rowSpacing={3}
        columnSpacing={3}>
        <Grid item xs={12} sm={6}>
          <Typography className={classes.summaryHeader}>Identity Type</Typography>
          <Typography>{identityType}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography className={classes.summaryHeader}>Account Type</Typography>
          <Typography>{accountType}</Typography>
        </Grid>
      </Grid>
      <Grid item width="100%">
        <Typography className={classes.summaryHeader}>Bank Name</Typography>
        <Typography>{bankName}</Typography>
      </Grid>
      <Grid item width="100%">
        <Typography className={classes.summaryHeader}>Bank Address</Typography>
        <Grid item width="100%">
          <Typography>{bankAddressValues.join(", ")}</Typography>
        </Grid>
      </Grid>
      <Grid
        container
        item
        justifyContent="space-between"
        flexDirection="row"
        rowSpacing={3}
        columnSpacing={3}>
        <Grid item xs={12} md={isUsdOrGbp ? 4 : 6}>
          <Typography className={classes.summaryHeader}>
            {isGbpOrEur ? "IBAN" : "Account Number"}
          </Typography>
          <Typography>{ibanAccNumber}</Typography>
        </Grid>
        {transitNumber && (
          <Grid item xs={12} sm={6}>
            <Typography className={classes.summaryHeader}>Transit Number</Typography>
            <Typography>{transitNumber}</Typography>
          </Grid>
        )}
        {institutionNumber && (
          <Grid item xs={12} sm={6}>
            <Typography className={classes.summaryHeader}>Institution Number</Typography>
            <Typography>{institutionNumber}</Typography>
          </Grid>
        )}
        {abaRoutingNumber && (
          <Grid item xs={12} md={isUsd ? 4 : 6}>
            <Typography className={classes.summaryHeader}>ABA Routing Number</Typography>
            <Typography>{abaRoutingNumber}</Typography>
          </Grid>
        )}
        {sortCodeOrBsb && (
          <Grid item xs={12} md={isGbp ? 4 : 6}>
            <Typography className={classes.summaryHeader}>{isAud ? "BSB" : "Sort Code"}</Typography>
            <Typography>{sortCodeOrBsb}</Typography>
          </Grid>
        )}
        {swiftCode && (
          <Grid item xs={12} md={isUsdOrGbp ? 4 : 6}>
            <Typography className={classes.summaryHeader}>Swift Code</Typography>
            <Typography>{swiftCode}</Typography>
          </Grid>
        )}
        <ButtonContainer>
          <Button
            disableRipple
            variant="text"
            onClick={handleGoBack}
            className={classes.secondaryButton}>
            Back To Payment
          </Button>
          <Button
            disableRipple
            variant="text"
            onClick={handleAddDetails}
            className={classes.primaryButton}>
            Add Another Bank
          </Button>
        </ButtonContainer>
      </Grid>
    </>
  );
};
